// plan_strip — the plan as a row of clickable parts above the chat.
//
// ── WHY IT EXISTS ───────────────────────────────────────────────────────────
//
// The plan was first shipped as something to READ: a card in the feed, and each part had to be
// launched by pasting its text back in. The operator's answer, on seeing that: the parts belong in a
// strip above the chat, and you click the next one when you are happy with the last.
//
// That is a better design than either of the two it replaced, for a reason worth writing down: it
// leaves the decision with the person AND it removes a bug. Re-running the original compound request
// made the platform propose a WHOLE NEW PLAN and start again at part 1 — the log showed two `plan`
// events for one request. A click names the part, so nothing is re-decided.
//
// ── DERIVED, NOT STORED ─────────────────────────────────────────────────────
//
// The strip is a fold over the thread the UI already has: the `plan` item carries the parts, the
// `part` items carry what happened to them. No new request, nothing to keep in sync, and a reloaded
// page rebuilds the same strip from the same thread — which is also why this is a pure function with
// its own tests rather than state inside a component.
import type { Message } from "../api";

/** What a part looks like in the strip. */
export type StripPart = {
  id: string;
  title: string;
  /** Position, 1-based, for "3 / 10". */
  index: number;
  state: StripState;
  /** How long it took, ms — only for a part that finished and reported one. */
  ms: number | null;
};

export type StripState =
  /** Not started. */
  | "todo"
  /** A run is building it now. */
  | "running"
  /** Finished and sealed. */
  | "sealed"
  /** Finished red. */
  | "failed";

export type PlanStrip = {
  parts: StripPart[];
  /** How many are sealed, out of how many — the "3 / 10" the operator keeps asking for. */
  done: number;
  total: number;
  /**
   * Is a part in flight? While one is, NOTHING may be started: the platform holds one run per app and
   * would refuse a second with a conflict, so a strip that invited the click would be inviting an
   * error message.
   */
  busy: boolean;
};

/** No plan in this thread. The strip renders nothing rather than an empty frame. */
export const NO_PLAN: PlanStrip = { parts: [], done: 0, total: 0, busy: false };

/**
 * Fold the thread into a strip.
 *
 * ── THE LAST PLAN WINS ──
 *
 * A thread accumulates plans for as long as the app lives, and a part id (`p1`, `p2`) repeats from one
 * plan to the next. So the scan walks BACKWARD to the most recent `plan` item and reads only the part
 * markers after it. Without that, an old plan's sealed parts would make the current one look finished
 * before it started — the same rule, and the same reason, as plan_state's fold on the server.
 */
export function planStripOf(messages: readonly Message[]): PlanStrip {
  let planAt = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.kind === "plan") { planAt = i; break; }
  }
  if (planAt === -1) return NO_PLAN;

  // CLOSED plans have no strip. The operator put it away; leaving the row up would keep offering work
  // they have already declined, and the feed keeps the closing line that NAMES what was dropped — so
  // nothing disappears in silence, it just stops asking.
  for (let i = planAt + 1; i < messages.length; i++) {
    if (messages[i]!.kind === "plan-closed") return NO_PLAN;
  }

  // NOTE, after a mutation run: there is deliberately no early return for a plan with no parts. The
  // fold below already yields a strip of zero parts, which is what NO_PLAN is, and the component keys
  // off `total === 0`. A guard that cannot change the outcome is the "second mechanism for one
  // invariant" this codebase treats as the next divergence.
  const declared = messages[planAt]!.plan_parts ?? [];

  const state = new Map<string, { state: StripState; ms: number | null }>();
  for (let i = planAt + 1; i < messages.length; i++) {
    const m = messages[i]!;
    if (m.kind === "plan") break;                     // cannot happen after the last plan; explicit anyway
    if (m.kind !== "part") continue;
    const id = m.part_id;
    if (!id) continue;                                // an unmatched marker is disregarded, never guessed
    const s: StripState =
      m.part_state === "sealed" ? "sealed"
      : m.part_state === "failed" ? "failed"
      : "running";
    // LAST marker wins: a part that failed and was run again is what it is NOW, not what it was. A
    // first-wins fold would leave a repaired part showing red for ever.
    state.set(id, { state: s, ms: typeof m.part_ms === "number" ? m.part_ms : null });
  }

  const parts: StripPart[] = declared.map((p, i) => {
    const seen = state.get(p.id);
    return { id: p.id, title: p.title, index: i + 1, state: seen?.state ?? "todo", ms: seen?.ms ?? null };
  });

  return {
    parts,
    done: parts.filter((p) => p.state === "sealed").length,
    total: parts.length,
    busy: parts.some((p) => p.state === "running"),
  };
}

/**
 * May this part be started by a click?
 *
 * Three rules, each with a consequence the operator would otherwise meet as an error:
 *
 *   1. NOT while another part is running — the platform holds one run per app and refuses a second.
 *   2. NOT one that is already sealed. Re-running a finished part is not forbidden in principle, but
 *      it is not what a click on a green row means, and a click that rebuilt finished work would be
 *      the kind of surprise that makes someone stop trusting the strip.
 *   3. A FAILED part MAY be clicked. That is the whole point of the operator holding the decision:
 *      they look at what happened and try it again.
 *
 * Note what is NOT a rule: order. The operator may start part 3 before part 2 — it is their app, the
 * plan's order is advice about dependencies, not a gate. A part that needs something missing will
 * fail, and failing is information they asked for.
 */
export function canStart(strip: PlanStrip, partId: string): boolean {
  if (strip.busy) return false;
  const part = strip.parts.find((p) => p.id === partId);
  if (!part) return false;
  return part.state === "todo" || part.state === "failed";
}

/**
 * The part a click would most likely want next: the first unsealed one.
 *
 * Used to mark one row as the suggestion, not to restrict the others. Null when everything is sealed
 * or something is running.
 */
export function suggestedPart(strip: PlanStrip): StripPart | null {
  if (strip.busy) return null;
  return strip.parts.find((p) => p.state === "todo" || p.state === "failed") ?? null;
}
